import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import layout from './BasicInfo.module.css';
import box from './PatientInfoTab.module.css';
import styles from './CellBasicInfo.module.css';

// 면역세포·CAF Key 의 기본 정보 화면 (2026-11-05).
// 오가노이드 화면처럼 두 칸: 왼쪽 관련 오가노이드(같은 환자 = Key 앞 9자리) · 오른쪽 환자 정보(읽기 전용).
// 환자 정보 수정 · 치료이력 기간 보기 · 사진 · 약물반응 · CoA · PDF 는 이 화면에서 제공하지 않는다.
const restoreHashMark = (str) => (str ? String(str).replace(/hashmark/g, '#') : '');

// PatientInfoTab 과 같은 항목·순서
const PATIENT_FIELDS = [
    ['Patient Code', 'PatientCode'],
    ['Organism', 'organism'],
    ['Origin', 'orgin'],
    ['Gender', 'Gender'],
    ['Age', 'Age'],
    ['Stage', 'Stage'],
    ['Histologic Type', 'HistologicType'],
    ['Treatment History', 'TreatmentHistory'],
    ['Clinical Annotation', 'clinicalAnnotation'],
    ['Smoking', 'smoking'],
];

// 관련 오가노이드가 여럿이면 Patient_Info 도 여러 행이다. 값이 같으면 한 번, 다르면 오가노이드별로 보여준다.
function PatientValue({ rows, field }) {
    const norm = (v) => (v == null ? '' : String(v).trim());
    const values = rows.map((r) => norm(r[field]));
    if (values.every((v) => v === values[0])) return <>{rows[0][field]}</>;
    return (
        <div className={styles.perOrganoid}>
            {rows.map((r) => (
                <React.Fragment key={r.Key}>
                    <span className={styles.perOrganoidKey}>{restoreHashMark(r.Key)}</span>
                    <span>{r[field]}</span>
                </React.Fragment>
            ))}
        </div>
    );
}

function CellBasicInfo({ cellKey }) {
    const { t } = useTranslation();
    const [data, setData] = useState(null);

    useEffect(() => {
        setData(null);
        fetch('/api/getCellRelated', {
            method: 'POST',
            headers: { 'content-type': 'application/json' },
            body: JSON.stringify({ key: cellKey }),
        })
            .then((res) => (res.ok ? res.json() : { organoids: [], patients: [] }))
            .then((d) => setData({ organoids: d.organoids || [], patients: d.patients || [] }))
            .catch((err) => {
                console.error('Cell related fetch error:', err);
                setData({ organoids: [], patients: [] });
            });
    }, [cellKey]);

    if (!data) return <div className={layout.rightTabBox} />;

    return (
        <div className={layout.rightTabBox}>
            <div className={layout.twoPartBox}>
                <div className={box.outerBox}>
                    <div className={box.basicInformation}>
                        <span className={box.title}>Related Organoid</span>
                    </div>
                    <div className={box.contents}>
                        {data.organoids.length === 0 ? (
                            <div className={styles.empty}>{t('관련 오가노이드 없음')}</div>
                        ) : (
                            data.organoids.map((o) => (
                                <React.Fragment key={o.key}>
                                    <div className={box.leftContent}>
                                        <Link to={`/organoid/${o.key}`}>{restoreHashMark(o.key)}</Link>
                                    </div>
                                    <div className={`${box.rightContent} ${o.pending ? styles.note : ''}`}>
                                        {o.pending ? t('오가노이드 정보 없음 (뱅킹만 있음)') : (o.type || '')}
                                    </div>
                                </React.Fragment>
                            ))
                        )}
                    </div>
                </div>

                <div className={box.outerBox}>
                    <div className={box.basicInformation}>
                        <span className={box.title}>Patient Information</span>
                    </div>
                    <div className={box.contents}>
                        {data.patients.length === 0 ? (
                            <div className={styles.empty}>{t('환자 정보 없음')}</div>
                        ) : (
                            PATIENT_FIELDS.map(([label, field]) => (
                                <React.Fragment key={field}>
                                    <div className={box.leftContent}>{label}</div>
                                    <div className={box.rightContent}>
                                        <PatientValue rows={data.patients} field={field} />
                                    </div>
                                </React.Fragment>
                            ))
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}

export default CellBasicInfo;
